/**
 * Utilitários para gerenciar o sistema de votação
 */

export interface ParlamentarVoto {
  id: string;
  parlamentar: string;
  nome: string;
  aprovacao: "sim" | "nao" | "abstencao";
}

export interface PautaVotacaoData {
  opcoesVotacao?: string;
  statusAtual?: string;
  votos?: ParlamentarVoto[];
  votos1turno?: ParlamentarVoto[];
  votos2turno?: ParlamentarVoto[];
}

/**
 * Garante que os arrays de votos estejam inicializados corretamente
 */
export function initializeVotingArrays(
  data: PautaVotacaoData
): PautaVotacaoData {
  const result = { ...data };

  if (result.opcoesVotacao === "2-turnos") {
    result.votos1turno = Array.isArray(result.votos1turno)
      ? result.votos1turno
      : [];
    result.votos2turno = Array.isArray(result.votos2turno)
      ? result.votos2turno
      : [];
    // Limpar array de votos simples se existir
    if (result.votos) {
      delete result.votos;
    }
  } else if (result.opcoesVotacao === "simples") {
    result.votos = Array.isArray(result.votos) ? result.votos : [];
    // Limpar arrays de dois turnos se existirem
    if (result.votos1turno) {
      delete result.votos1turno;
    }
    if (result.votos2turno) {
      delete result.votos2turno;
    }
  }

  return result;
}

/**
 * Valida se é possível passar para o próximo turno de votação
 */
export function canProceedToNextTurn(data: PautaVotacaoData): boolean {
  if (data.opcoesVotacao !== "2-turnos") {
    return false;
  }

  const votos1turno = Array.isArray(data.votos1turno) ? data.votos1turno : [];
  const votos2turno = Array.isArray(data.votos2turno) ? data.votos2turno : [];

  if (data.statusAtual === "votacao1turno") {
    return votos1turno.length > 0;
  }

  if (
    (data.statusAtual === "votado" || data.statusAtual === "finalizada") &&
    votos1turno.length > 0 &&
    votos2turno.length === 0
  ) {
    return true;
  }

  return false;
}

/**
 * Obtém o array de votos correto baseado no status atual
 */
export function getCurrentVotingArray(
  data: PautaVotacaoData
): ParlamentarVoto[] {
  const initializedData = initializeVotingArrays(data);

  if (initializedData.opcoesVotacao === "2-turnos") {
    const votos1turno = initializedData.votos1turno || [];
    const votos2turno = initializedData.votos2turno || [];

    if (initializedData.statusAtual === "votacao1turno") {
      return votos1turno;
    } else if (initializedData.statusAtual === "votacao2turno") {
      return votos2turno;
    }

    if (
      (initializedData.statusAtual === "votado" ||
        initializedData.statusAtual === "finalizada") &&
      votos1turno.length > 0 &&
      votos2turno.length === 0
    ) {
      return votos1turno;
    }

    if (votos2turno.length > 0) {
      return votos2turno;
    }

    if (votos1turno.length > 0) {
      return votos1turno;
    }
  } else if (initializedData.opcoesVotacao === "simples") {
    return initializedData.votos || [];
  }

  return [];
}

/**
 * Adiciona um voto ao array correto baseado no status atual
 */
export function addVoteToCorrectArray(
  data: PautaVotacaoData,
  vote: ParlamentarVoto
): PautaVotacaoData {
  const result = initializeVotingArrays(data);

  if (result.opcoesVotacao === "2-turnos") {
    if (result.statusAtual === "votacao1turno") {
      result.votos1turno = [...(result.votos1turno || []), vote];
    } else if (result.statusAtual === "votacao2turno") {
      result.votos2turno = [...(result.votos2turno || []), vote];
    }
  } else if (result.opcoesVotacao === "simples") {
    result.votos = [...(result.votos || []), vote];
  }

  return result;
}

/**
 * Remove um voto do array correto baseado no status atual
 */
export function removeVoteFromCorrectArray(
  data: PautaVotacaoData,
  parlamentarId: string
): PautaVotacaoData {
  const result = initializeVotingArrays(data);

  if (result.opcoesVotacao === "2-turnos") {
    if (result.statusAtual === "votacao1turno") {
      result.votos1turno = (result.votos1turno || []).filter(
        (voto) => voto.parlamentar !== parlamentarId
      );
    } else if (result.statusAtual === "votacao2turno") {
      result.votos2turno = (result.votos2turno || []).filter(
        (voto) => voto.parlamentar !== parlamentarId
      );
    }
  } else if (result.opcoesVotacao === "simples") {
    result.votos = (result.votos || []).filter(
      (voto) => voto.parlamentar !== parlamentarId
    );
  }

  return result;
}

/**
 * Conta os votos de um array específico
 */
export function countVotes(votes: ParlamentarVoto[]): {
  sim: number;
  nao: number;
  abstencao: number;
} {
  return votes.reduce(
    (acc, vote) => {
      acc[vote.aprovacao]++;
      return acc;
    },
    { sim: 0, nao: 0, abstencao: 0 }
  );
}

/**
 * Determina o status de votação para exibição na interface
 */
export function getVotingStatusDisplay(
  votada: boolean | undefined | string,
  opcoesVotacao?: string,
  statusAtual?: string
): {
  text: string;
  color: "success" | "warning" | "info" | "error";
} {
  const statusNormalizado =
    typeof statusAtual === "string" ? statusAtual.toLowerCase() : statusAtual;

  // Caso especial: votada é false mas tem configuração de dois turnos e está no primeiro turno
  if (
    votada === false &&
    opcoesVotacao === "2-turnos" &&
    statusNormalizado === "votacao1turno"
  ) {
    return { text: "1º Turno", color: "warning" };
  }

  if (
    votada === false &&
    opcoesVotacao === "2-turnos" &&
    statusNormalizado === "votacao2turno"
  ) {
    return { text: "2º Turno", color: "info" };
  }

  if (statusNormalizado === "votado" || statusNormalizado === "finalizada") {
    return {
      text: opcoesVotacao === "2-turnos" ? "Votado (2 turnos)" : "Votado",
      color: "success",
    };
  }

  if (votada === true) {
    if (opcoesVotacao === "2-turnos") {
      switch (statusNormalizado) {
        case "votacao1turno":
          return { text: "1º Turno", color: "warning" };
        case "votacao2turno":
          return { text: "2º Turno", color: "info" };
        case "votado":
        case "finalizada":
          return { text: "Votado (2 turnos)", color: "success" };
        default:
          return { text: "Votado (2 turnos)", color: "success" };
      }
    } else {
      return { text: "Votado", color: "success" };
    }
  } else if (votada === undefined) {
    return { text: "Não votado", color: "error" };
  } else if (votada === "") {
    return { text: "Não informado", color: "info" };
  } else {
    return { text: "Não informado", color: "info" };
  }
}
